import { hashId } from "./util.js";
import { ClusterCounter, deriveMessageId } from "./message-id.js";
import type { BiliMessage } from "./bili-message.js";

// Gemini native wire (`generativelanguage.googleapis.com`, /v1beta/models/<m>:
// generateContent|streamGenerateContent|countTokens). Two properties of this
// dialect drive the whole codec:
//   - Gemini verifies what it sent: thoughtSignatures, functionCall ids and
//     every part variant it may re-emit must come back byte-identical, so a
//     part the core cannot represent field-by-field rides verbatim in
//     `BiliMessage.rawGoogleParts`.
//   - Gemini rejects non-alternating roles, so the reverse mapping MERGES
//     runs of same-side cores into one content (see coreToGoogle).

export type GooglePart = {
  text?: string;
  thought?: boolean;
  thoughtSignature?: string;
  functionCall?: { name: string; args?: unknown; id?: string };
  functionResponse?: {
    name: string;
    response?: unknown;
    id?: string;
    parts?: GooglePart[];
  };
  inlineData?: { mimeType: string; data: string };
  fileData?: { fileUri: string; mimeType?: string };
  [k: string]: unknown;
};

export type GoogleContent = { role?: string; parts: GooglePart[] };

export type GoogleFunctionDeclaration = {
  name: string;
  description?: string;
  parameters?: unknown;
  parametersJsonSchema?: unknown;
  response?: unknown;
  [k: string]: unknown;
};

export type GoogleTool = {
  functionDeclarations?: GoogleFunctionDeclaration[];
  [k: string]: unknown;
};

export type GoogleSystemInstruction = { parts?: GooglePart[]; role?: string };

export type GoogleRequestBody = {
  contents: GoogleContent[];
  systemInstruction?: GoogleSystemInstruction;
  tools?: GoogleTool[];
  toolConfig?: unknown;
  generationConfig?: Record<string, unknown>;
  cachedContent?: string;
  [k: string]: unknown;
};

export type GoogleFlat = { msgs: BiliMessage[]; systemText: string };

/** Hoist the system dimension out of the fold space. `systemInstruction` is
 *  host runtime state exactly like the openai codec's leading system prefix
 *  (see openaiToCore): its content varies across restarts, so keeping it
 *  inside the id space made every downstream fingerprint spanning it
 *  unstable, and a compress range that covered it deleted the model's system
 *  prompt from the rebuilt wire. The adapter re-injects it as
 *  `systemInstruction` at egress. */
export function googleSystemText(body: GoogleRequestBody): string {
  const parts = body.systemInstruction?.parts;
  if (!Array.isArray(parts)) return "";
  const texts: string[] = [];
  for (const part of parts) {
    if (typeof part.text === "string" && part.text.length > 0)
      texts.push(part.text);
  }
  return texts.join("\n\n");
}

/** Map a Gemini request body onto the kernel's message space. Mirrors
 *  openaiToCore's role handling: `content.role === "model"` is the assistant
 *  side, everything else the user side. A user content's functionResponse
 *  parts expand into `role:"tool"` cores emitted BEFORE that content's own
 *  user core — the client appends a second functionResponse to the previous
 *  user content instead of opening a new one, so those parts belong to the
 *  tool pair, not to the user text they sit next to. */
export function googleToCore(body: GoogleRequestBody): GoogleFlat {
  const msgs: BiliMessage[] = [];
  const clusters = new ClusterCounter();
  // name -> toolCallIds of calls not yet answered, in wire order. A
  // functionResponse without an id pairs with the EARLIEST unmatched call of
  // the same name; calls always precede their responses on the wire, so one
  // forward pass is enough and the pairing is deterministic across passes.
  const unmatchedCalls = new Map<string, string[]>();
  const contents = Array.isArray(body.contents) ? body.contents : [];

  for (const content of contents) {
    const parts = Array.isArray(content.parts) ? content.parts : [];
    const isModel = content.role === "model";

    // 1. Tool results first: the rebuilt user content must lead with its
    //    functionResponse parts.
    for (const part of parts) {
      const fr = part.functionResponse;
      if (!fr || typeof fr.name !== "string") continue;
      const text = jsonText(fr.response ?? {});
      const wireId = typeof fr.id === "string" ? fr.id : undefined;
      const queue = unmatchedCalls.get(fr.name);
      let paired: string | undefined;
      if (wireId) {
        // An explicitly tagged response answers that call, so a later
        // id-less response of the same name cannot claim it.
        const i = queue ? queue.indexOf(wireId) : -1;
        if (queue && i >= 0) queue.splice(i, 1);
      } else if (queue && queue.length > 0) {
        paired = queue.shift();
      }
      const base = deriveMessageId("tool", "tool-result", text, {
        toolCallId: wireId ?? paired ?? "",
        toolName: fr.name,
      });
      const id = clusters.next(base);
      msgs.push({
        id,
        role: "tool",
        contentType: "tool-result",
        toolName: fr.name,
        // No wire id and nothing to pair with: the core's own id
        // stands in, so the response is still identifiable and stable.
        toolCallId: wireId ?? paired ?? id,
        text,
        rawGoogleParts: [part],
      });
    }

    // 2. Thinking parts.
    for (const part of parts) {
      if (part.thought !== true || typeof part.text !== "string") continue;
      const base = deriveMessageId("assistant", "reasoning", part.text);
      msgs.push({
        id: clusters.next(base),
        role: "assistant",
        contentType: "reasoning",
        text: part.text,
        rawGoogleParts: [part],
        ...signatureField("googleThoughtSignature", part.thoughtSignature),
      });
    }

    // 3. The content's own text/media core. A content made only of
    //    functionResponse parts yields none (its parts ride on the tool
    //    cores), so no empty user core is invented.
    const rest = parts.filter(isContentPart);
    if (rest.length > 0) {
      const text = textPartsOf(rest)
        .map((p) => p.text ?? "")
        .join("\n");
      const inline = inlineDataOf(rest);
      const base = deriveMessageId(
        isModel ? "assistant" : "user",
        "text",
        text,
      );
      msgs.push({
        id: clusters.next(base),
        role: isModel ? "assistant" : "user",
        contentType: "text",
        text,
        ...(inline
          ? { imageMediaType: inline.mimeType, imageBase64: inline.data }
          : {}),
        rawGoogleParts: rest,
        ...signatureField(
          "googleThoughtSignature",
          textPartsOf(rest).find((p) => typeof p.thoughtSignature === "string")
            ?.thoughtSignature,
        ),
      });
    }

    // 4. Function calls last, so a call core sits immediately before the
    //    tool-result cores that answer it. Without a wire id the
    //    synthesized toolCallId is the core's own id, which its
    //    functionResponse reuses.
    for (const part of parts) {
      const fc = part.functionCall;
      if (!fc || typeof fc.name !== "string") continue;
      const text = jsonText(fc.args ?? {});
      const wireId = typeof fc.id === "string" ? fc.id : undefined;
      const base = deriveMessageId("assistant", "tool-call", text, {
        toolCallId: wireId ?? "",
        toolName: fc.name,
      });
      const id = clusters.next(base);
      const toolCallId = wireId ?? id;
      const queue = unmatchedCalls.get(fc.name);
      if (queue) queue.push(toolCallId);
      else unmatchedCalls.set(fc.name, [toolCallId]);
      msgs.push({
        id,
        role: "assistant",
        contentType: "tool-call",
        toolName: fc.name,
        toolCallId,
        text,
        rawGoogleParts: [part],
        ...signatureField("googleThoughtSignature", part.thoughtSignature),
      });
    }
  }

  return { msgs, systemText: googleSystemText(body) };
}

/** Rebuild Gemini `contents` from kernel messages. Runs of same-side cores
 *  are merged into ONE content (Gemini rejects non-alternating roles): an
 *  assistant run becomes a single `{"role":"model"}` content whose parts are
 *  ordered reasoning, text, tool-call; a user-side run (user + tool-result)
 *  becomes a single `{"role":"user"}` content whose functionResponse parts
 *  lead. Mid-conversation system cores (host-synthetic; Gemini has no system
 *  role inside contents) ride on the user side as a text part. */
export function coreToGoogle(messages: BiliMessage[]): GoogleContent[] {
  const out: GoogleContent[] = [];
  let model: {
    reasoning: GooglePart[];
    text: GooglePart[];
    calls: GooglePart[];
  } | null = null;
  let user: { responses: GooglePart[]; content: GooglePart[] } | null = null;
  const flushModel = () => {
    if (!model) return;
    const parts = [...model.reasoning, ...model.text, ...model.calls];
    model = null;
    if (parts.length > 0) out.push({ role: "model", parts });
  };
  const flushUser = () => {
    if (!user) return;
    const parts = [...user.responses, ...user.content];
    user = null;
    if (parts.length > 0) out.push({ role: "user", parts });
  };
  for (const m of messages) {
    if (m.role === "assistant") {
      flushUser();
      if (!model) model = { reasoning: [], text: [], calls: [] };
      if (m.contentType === "reasoning") model.reasoning.push(reasoningPart(m));
      else if (m.contentType === "tool-call") model.calls.push(toolCallPart(m));
      else model.text.push(...contentParts(m));
    } else {
      flushModel();
      if (!user) user = { responses: [], content: [] };
      if (m.role === "tool" || m.contentType === "tool-result")
        user.responses.push(...toolResultParts(m));
      else user.content.push(...contentParts(m));
    }
  }
  flushModel();
  flushUser();
  return out;
}

/** Text-protocol/fallback system injection. `contents` has no top-level
 *  system slot, so the prompt becomes the first part of the leading user
 *  content, or a new leading user content when none is there. The adapter and
 *  the server inject the real system via `systemInstruction` instead (see the
 *  proxy's createGoogleAdapter) — this exists for the path where no system
 *  channel is wanted. */
export function injectGoogleSystem(
  contents: GoogleContent[],
  parts: string[],
): GoogleContent[] {
  if (parts.length === 0) return contents;
  const extra = parts.join("\n\n");
  const head = contents[0];
  if (head && head.role === "user")
    return [
      { ...head, parts: [{ text: extra }, ...head.parts] },
      ...contents.slice(1),
    ];
  return [{ role: "user", parts: [{ text: extra }] }, ...contents];
}

/** Extract the conversation dimension for Gemini: a client-provided session
 *  header if present (omp sends none for this protocol), else a content
 *  fingerprint of the first user content. See conversationSignalAnthropic for
 *  the full rationale. */
export function conversationSignalGoogle(
  body: GoogleRequestBody,
  headerValue?: string,
): string {
  if (headerValue && headerValue.trim()) return headerValue.trim();
  const contents = Array.isArray(body.contents) ? body.contents : [];
  const firstUser = contents.find((c) => c.role !== "model");
  const parts =
    firstUser && Array.isArray(firstUser.parts) ? firstUser.parts : [];
  const seed = firstUser
    ? parts.map((p) => (typeof p.text === "string" ? p.text : "")).join("\n")
    : "default";
  return hashId(seed);
}

/** A reasoning core: its raw thought part verbatim (text refreshed — the
 *  kernel re-renders core text with ref tags/truncation), else a
 *  reconstructed `{thought:true}` part carrying the sidecar signature. */
function reasoningPart(m: BiliMessage): GooglePart {
  const text = m.text ?? "";
  const head = rawParts(m)[0];
  if (head) return { ...head, text, thought: true };
  return {
    text,
    thought: true,
    ...signatureField("thoughtSignature", m.googleThoughtSignature),
  };
}

function toolCallPart(m: BiliMessage): GooglePart {
  const head = rawParts(m)[0];
  if (head && head.functionCall) return head;
  return {
    functionCall: {
      name: m.toolName ?? "unknown",
      args: jsonValue(m.text),
      ...(m.toolCallId ? { id: m.toolCallId } : {}),
    },
    ...signatureField("thoughtSignature", m.googleThoughtSignature),
  };
}

function toolResultParts(m: BiliMessage): GooglePart[] {
  const raw = rawParts(m);
  if (raw.length > 0 && raw.every((p) => p.functionResponse)) return raw;
  return [
    {
      functionResponse: {
        name: m.toolName ?? "unknown",
        response: jsonValue(m.text),
        ...(m.toolCallId ? { id: m.toolCallId } : {}),
      },
    },
  ];
}

/** Parts for a text/media core. Raw parts are preferred verbatim — they carry
 *  signatures, inlineData/fileData, videoMetadata and any unknown field the
 *  wire sent — EXCEPT where they carry the text the kernel owns: core text is
 *  rewritten in flight (ref tags, truncation, absorb prompts), so a raw text
 *  part must never override it. Unchanged text means the raw array goes out
 *  untouched; changed text means the text parts are re-rendered from the core
 *  text while every non-text raw part stays verbatim. */
function contentParts(m: BiliMessage): GooglePart[] {
  const text = m.text ?? "";
  const raw = rawParts(m);
  if (raw.length === 0) {
    const parts: GooglePart[] = text
      ? [
          {
            text,
            ...signatureField("thoughtSignature", m.googleThoughtSignature),
          },
        ]
      : [];
    // The image sidecars are separate from the raw parts, so an image
    // survives even a core that lost its raw array (rebased/persisted).
    if (m.imageBase64 && m.imageMediaType)
      parts.push({
        inlineData: { mimeType: m.imageMediaType, data: m.imageBase64 },
      });
    return parts;
  }
  const rawTexts = textPartsOf(raw);
  if (rawTexts.map((p) => p.text ?? "").join("\n") === text) return raw;
  const parts: GooglePart[] = text
    ? [
        {
          text,
          ...signatureField("thoughtSignature", m.googleThoughtSignature),
        },
      ]
    : [];
  for (const part of raw) {
    if (!rawTexts.includes(part)) parts.push(part);
  }
  return parts;
}

function rawParts(m: BiliMessage): GooglePart[] {
  const raw = m.rawGoogleParts;
  if (!Array.isArray(raw)) return [];
  const out: GooglePart[] = [];
  for (const item of raw) {
    if (typeof item === "object" && item !== null) out.push(item as GooglePart);
  }
  return out;
}

/** The parts whose text the core projects into `BiliMessage.text`. */
function textPartsOf(parts: GooglePart[]): GooglePart[] {
  return parts.filter((p) => typeof p.text === "string" && p.thought !== true);
}

/** Parts a text/media core owns: everything that is not a tool pair member or
 *  a thinking part with text (which get cores of their own). */
function isContentPart(part: GooglePart): boolean {
  if (part.functionCall !== undefined || part.functionResponse !== undefined)
    return false;
  if (part.thought === true && typeof part.text === "string") return false;
  return true;
}

function inlineDataOf(
  parts: GooglePart[],
): { mimeType: string; data: string } | undefined {
  for (const part of parts) {
    const data = part.inlineData;
    if (
      data &&
      typeof data.mimeType === "string" &&
      typeof data.data === "string"
    )
      return data;
  }
  return undefined;
}

/** Gemini's signature field is `thoughtSignature` on the wire but
 *  `googleThoughtSignature` on the core sidecar; each direction derives it from
 *  what the other end carried. */
function signatureField(
  key: "thoughtSignature" | "googleThoughtSignature",
  signature: unknown,
): { [k: string]: string } {
  return typeof signature === "string" ? { [key]: signature } : {};
}

/** The wire's args/response are JSON objects; the core carries them as text so
 *  the kernel's text pipeline (truncation, refs, absorb) can render them. */
function jsonText(value: unknown): string {
  try {
    return JSON.stringify(value) ?? "";
  } catch {
    return "";
  }
}

/** Reverse of jsonText on the reconstruction path (a core with no raw parts).
 *  Unparseable text — a host set the core text by hand — degrades to
 *  `{ output: text }` instead of dropping the payload. */
function jsonValue(text: string | undefined): unknown {
  if (!text) return {};
  try {
    const value: unknown = JSON.parse(text);
    return value === null ? {} : value;
  } catch {
    return { output: text };
  }
}
